import { mutation } from "../_generated/server";
import { v } from "convex/values";
import { ConvexError } from "convex/values";

// Validation constants
const MAX_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_PROMPT_LENGTH = 2000;

export const createWritingStyle = mutation({
  args: {
    name: v.string(),
    description: v.string(),
    systemPrompt: v.string(),
    isCustom: v.boolean(),
  },
  handler: async (ctx, args) => {
    const identity = await ctx.auth.getUserIdentity();
    if (!identity) {
      throw new Error("Unauthenticated");
    }
    
    const userId = identity.subject;
    
    // Validate input
    if (!args.name.trim()) {
      throw new ConvexError("Name is required");
    }
    
    if (args.name.length > MAX_NAME_LENGTH) {
      throw new ConvexError(`Name must be ${MAX_NAME_LENGTH} characters or less`);
    }
    
    if (!args.description.trim()) {
      throw new ConvexError("Description is required");
    }
    
    if (args.description.length > MAX_DESCRIPTION_LENGTH) {
      throw new ConvexError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
    }
    
    if (!args.systemPrompt.trim()) {
      throw new ConvexError("System prompt is required");
    }
    
    if (args.systemPrompt.length > MAX_PROMPT_LENGTH) {
      throw new ConvexError(`System prompt must be ${MAX_PROMPT_LENGTH} characters or less`);
    }

    // Get user's existing styles to check limit
    const existingStyles = await ctx.db
      .query("writingStyles")
      .withIndex("by_userId", (q) => q.eq("userId", userId))
      .collect();
      
    // Limit number of custom styles to 10 per user
    if (existingStyles.length >= 10) {
      throw new ConvexError("You have reached the maximum limit of 10 custom writing styles");
    }

    return await ctx.db.insert("writingStyles", {
      userId,
      name: args.name.trim(),
      description: args.description.trim(),
      systemPrompt: args.systemPrompt.trim(),
      isCustom: args.isCustom,
      createdAt: Date.now(),
    });
  },
});

export const updateWritingStyle = mutation({
  args: {
    id: v.id("writingStyles"),
    name: v.string(),
    description: v.string(),
    systemPrompt: v.string(),
  },
  handler: async (ctx, args) => {
    const identity = await ctx.auth.getUserIdentity();
    if (!identity) {
      throw new Error("Unauthenticated");
    }
    
    const userId = identity.subject;

    // Validate inputs
    if (!args.name.trim()) {
      throw new ConvexError("Name is required");
    }
    
    if (args.name.length > MAX_NAME_LENGTH) {
      throw new ConvexError(`Name must be ${MAX_NAME_LENGTH} characters or less`);
    }
    
    if (!args.description.trim()) {
      throw new ConvexError("Description is required");
    }
    
    if (args.description.length > MAX_DESCRIPTION_LENGTH) {
      throw new ConvexError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
    }
    
    if (!args.systemPrompt.trim()) {
      throw new ConvexError("System prompt is required");
    }
    
    if (args.systemPrompt.length > MAX_PROMPT_LENGTH) {
      throw new ConvexError(`System prompt must be ${MAX_PROMPT_LENGTH} characters or less`);
    }

    const style = await ctx.db.get(args.id);
    if (!style || style.userId !== identity.subject) {
      throw new Error("Writing style not found or unauthorized");
    }

    await ctx.db.patch(args.id, {
      name: args.name.trim(),
      description: args.description.trim(),
      systemPrompt: args.systemPrompt.trim(),
    });
  },
});

export const deleteWritingStyle = mutation({
  args: {
    id: v.id("writingStyles"),
  },
  handler: async (ctx, args) => {
    const identity = await ctx.auth.getUserIdentity();
    if (!identity) {
      throw new Error("Unauthenticated");
    }

    const style = await ctx.db.get(args.id);
    if (!style || style.userId !== identity.subject) {
      throw new Error("Writing style not found or unauthorized");
    }

    await ctx.db.delete(args.id);
  },
});